import {
  PrimaryControlsStripBackControl,
  usePrimaryControlsStripBackGate,
  usePrimaryControlsStripHasWideBackControl,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import {
  PrimaryControlsStripExpansionHost,
  usePrimaryControlsStripExpansionAnimatedHeight,
  usePrimaryControlsStripExpansionHasContent,
  usePrimaryControlsStripExpansionState,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.expansion";
import {
  PrimaryControlsStripTitleHost,
  usePrimaryControlsStripTitleHostGate,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import {
  NavContext,
  PERMANENT_SIDEBAR_WIDTH,
  RAIL_SIDEBAR_WIDTH,
  type NavSidebarMode,
  useIsPrimaryControlsStripExpansionButtonHidden,
} from "@-/dev.improve.haniverse.routes.nav._context";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import type { DrawerHeaderProps } from "@-/com.npmjs.expo-router/drawer";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { PRIMARY_CONTROLS_STRIP_HEIGHT } from "@-/dev.improve.haniverse.lib.nav-chrome.entities";
import { useMemo } from "react";
import { Platform, Pressable, View } from "@-/com.npmjs.react-native";
import Animated from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";

export type PrimaryControlsStripLayout = "default" | "sidebar";

export type PrimaryControlsStripProps = Omit<Partial<DrawerHeaderProps>, "layout"> & {
  layout?: PrimaryControlsStripLayout;
  /**
   * Web sidebar layout only: render the sidebar expand/collapse toggle as the left-most
   * control in the header. The header spans the full page width above the sidebar+scene,
   * so collapsing/expanding the sidebar beneath it leaves the header layout untouched.
   */
  showSidebarToggle?: boolean;
};

function getWebSidebarHeaderLeadingWidth(sidebarMode: NavSidebarMode) {
  if (sidebarMode === "permanent") {
    return PERMANENT_SIDEBAR_WIDTH;
  }
  if (sidebarMode === "rail") {
    return RAIL_SIDEBAR_WIDTH;
  }
  return undefined;
}

export function PrimaryControlsStrip({
  layout = "default",
  showSidebarToggle = false,
  ...props
}: PrimaryControlsStripProps) {
  const hasExpansionContent = usePrimaryControlsStripExpansionHasContent();
  // A focused screen can suppress the expand/collapse toggle (e.g. the welcome slide, via
  // `<SlideOptions isPrimaryControlsStripExpansionButtonHidden />`) while keeping its registered content.
  const isPrimaryControlsStripExpansionButtonHidden =
    useIsPrimaryControlsStripExpansionButtonHidden();
  // Expand/collapse state lives in app context so the sidebar toggle (landscape) and the
  // scene panel stay in sync. `hasContent` gates strip height when there is nothing to show.
  const { isExpanded, toggleExpanded } = usePrimaryControlsStripExpansionState();
  const insets = useSafeAreaInsets();
  const [sidebarMode, toggleSidebar] = useContextSelectors(
    NavContext,
    (c) => c.sidebarMode,
    (c) => c.toggleSidebar,
  );
  // The sidebar toggle is opted in by the web sidebar layout (which renders the header
  // full-width above the sidebar). "permanent" is the only expanded web mode; "rail" is
  // collapsed, so the icon mirrors the open/closed state.
  const sidebarOpen = sidebarMode === "permanent";

  // App context defaults sidebar mode to "overlay", which also matches native — only
  // surface the menu button when we have a drawer navigation handle to actually open it.
  const navigation = props?.navigation;
  const canOpenSidebar =
    Platform.OS === "web" && sidebarMode === "overlay" && navigation !== undefined;

  const controlAlignment = Platform.OS === "web" ? "items-center" : "items-start";

  const isSidebarLayout = layout === "sidebar";
  // Native landscape uses a full-width top safe-area strip from root layout; only absorb
  // `insets.top` in the default header (portrait tabs / web drawer screen header).
  const includeTopSafeArea = !isSidebarLayout;
  const topSafeAreaPadding = includeTopSafeArea ? insets.top : 0;
  const usesWebSidebarTitleLayout = Platform.OS === "web" && !isSidebarLayout && showSidebarToggle;

  // Wide back controls (a label or custom node) still need a bounded lane so they
  // truncate before swallowing the row. The title itself is not part of this flex
  // sizing anymore; native/default layouts center it with an absolute overlay below.
  const hasWideBackControl = usePrimaryControlsStripHasWideBackControl();
  // The default and sidebar headers each own their single back slot: placement-agnostic.
  const backGate = usePrimaryControlsStripBackGate();
  const titleHostGate = usePrimaryControlsStripTitleHostGate();
  // The right column's only default-layout content is the expand/collapse chevron.
  const showsExpansionToggle = hasExpansionContent && !isPrimaryControlsStripExpansionButtonHidden;
  // With a wide back control, grow the left/right lanes equally so the back label
  // truncates within its half of the toolbar. Narrow icon-only controls shrink to
  // their intrinsic size.
  const defaultColumnGrowClassName = hasWideBackControl ? "flex-1 " : "";
  const defaultLeftColumnClassName = `${defaultColumnGrowClassName}h-full min-w-0 flex-row ${controlAlignment} justify-start`;
  const defaultRightColumnClassName = `${defaultColumnGrowClassName}h-full min-w-0 flex-row ${controlAlignment} justify-end`;
  const webSidebarHeaderLeadingWidth = usesWebSidebarTitleLayout
    ? getWebSidebarHeaderLeadingWidth(sidebarMode)
    : undefined;

  const containerStyle = useMemo(
    () =>
      includeTopSafeArea
        ? {
            overflow: "visible" as const,
            paddingTop: topSafeAreaPadding,
          }
        : {
            overflow: "visible" as const,
          },
    [includeTopSafeArea, topSafeAreaPadding],
  );

  const rowStyle = useMemo(
    () => ({ height: PRIMARY_CONTROLS_STRIP_HEIGHT, overflow: "visible" as const }),
    [],
  );

  // Shared with `PrimaryControlsStripExpansionPanel` (native landscape): the expand-down/collapse-up height
  // animation, the offscreen layout probe, and the content-loss gating all live in the hook so
  // both callsites stay in lockstep.
  const { onContentLayout: handleExpansionContentLayout, wrapperStyle: expansionWrapperStyle } =
    usePrimaryControlsStripExpansionAnimatedHeight();

  return (
    <View
      className="relative overflow-visible border-b border-border bg-background"
      style={containerStyle}
    >
      <View
        className={
          isSidebarLayout
            ? "relative flex-row justify-between overflow-visible px-2"
            : "relative flex-row justify-between overflow-visible px-3"
        }
        style={rowStyle}
      >
        {/* Native/default layouts keep the title absolutely centered across the full
            toolbar so left/right controls can never move its horizontal center. Web
            sidebar layout still reserves the sidebar width on the left and
            left-aligns the title at the scene edge. */}
        {isSidebarLayout || !usesWebSidebarTitleLayout ? (
          <View
            className={
              isSidebarLayout
                ? "pointer-events-none absolute inset-0 flex-row items-center justify-center px-1"
                : "pointer-events-none absolute inset-0 flex-row items-center justify-center px-14"
            }
          >
            {titleHostGate !== null && (
              <PrimaryControlsStripTitleHost gate={titleHostGate} layout={layout} />
            )}
          </View>
        ) : null}
        <View
          className={
            isSidebarLayout
              ? `h-full flex-row ${controlAlignment} justify-start`
              : usesWebSidebarTitleLayout
                ? "h-full min-w-0 flex-row items-center justify-start"
                : defaultLeftColumnClassName
          }
          style={
            webSidebarHeaderLeadingWidth === undefined
              ? undefined
              : { width: webSidebarHeaderLeadingWidth }
          }
        >
          {backGate !== null && <PrimaryControlsStripBackControl gate={backGate} layout={layout} />}
          {showSidebarToggle ? (
            // `-ml-2` (-8px) pulls the toggle so its icon center sits 24px from the page edge,
            // exactly aligned with the column of sidebar tab icons below it. (Header padding
            // px-3=12px + half the 40px button = 32px would otherwise leave it 8px too far right.)
            <Pressable
              accessibilityLabel={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              accessibilityRole="button"
              className="-ml-2 size-10 items-center justify-center rounded-full active:bg-accent"
              hitSlop={10}
              onPress={toggleSidebar}
            >
              <Icon
                className="text-foreground"
                name={sidebarOpen ? "TbLayoutSidebarFilled" : "TbLayoutSidebar"}
                size={24}
              />
            </Pressable>
          ) : null}
          {canOpenSidebar ? (
            <Pressable
              accessibilityLabel="Open navigation menu"
              className="size-10 items-center justify-center rounded-full active:bg-accent"
              hitSlop={10}
              onPress={() => navigation?.toggleDrawer()}
            >
              <Icon className="text-foreground" name="MdMenu" size={24} />
            </Pressable>
          ) : null}
        </View>
        {isSidebarLayout || !usesWebSidebarTitleLayout ? null : (
          <View className="pointer-events-none h-full min-w-0 flex-1 flex-row items-center justify-start px-1">
            {titleHostGate !== null && (
              <PrimaryControlsStripTitleHost align="start" gate={titleHostGate} layout={layout} />
            )}
          </View>
        )}
        <View
          className={
            isSidebarLayout
              ? `h-full flex-row ${controlAlignment} justify-end`
              : usesWebSidebarTitleLayout
                ? `h-full flex-row ${controlAlignment} justify-end`
                : defaultRightColumnClassName
          }
        >
          {/* In sidebar (native landscape) layout the strip slides down over the scene via
              PrimaryControlsStripExpansionPanel rather than expanding within the header; the toggle sits
              to the right of the title so it doesn't crowd the back control on the left. */}
          {showsExpansionToggle ? (
            <Pressable
              accessibilityLabel={
                isExpanded ? "Collapse primary controls" : "Expand primary controls"
              }
              className="size-10 items-center justify-center overflow-hidden rounded-full active:bg-accent"
              hitSlop={10}
              onPress={toggleExpanded}
            >
              <Icon className="text-foreground" name="RiExpandUpDownFill" size={20} />
            </Pressable>
          ) : null}
        </View>
      </View>
      {isSidebarLayout ? null : (
        <Animated.View className="relative overflow-hidden" style={expansionWrapperStyle}>
          <View className="absolute inset-x-0 px-3" onLayout={handleExpansionContentLayout}>
            <PrimaryControlsStripExpansionHost />
          </View>
        </Animated.View>
      )}
    </View>
  );
}
